import {useEffect, useState} from 'react';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {MemberLifecycleDialogFrame} from '@client/pages/client/member-profile/MemberLifecycleDialogFrame';

/**
 * Standard household-composition tiers. VBA stores `Tier` as a 2-character
 * code.
 */
const TIER_OPTIONS: {value: string; label: string}[] = [
  {value: 'EE', label: 'EE — Employee only'},
  {value: 'ES', label: 'ES — Employee + spouse'},
  {value: 'EC', label: 'EC — Employee + child(ren)'},
  {value: 'FA', label: 'FA — Family'},
];

interface ChangeTierDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  groupId: string;
  memberId: string; // composed `${subscriberId}-${memberSeq}`
  /** Product whose enrollment rows carry the tier. */
  productId: string;
  productName: string;
  currentTier: string | null;
}

export function ChangeTierDialog({
  open,
  onClose,
  onSuccess,
  groupId,
  memberId,
  productId,
  productName,
  currentTier,
}: ChangeTierDialogProps) {
  const {toast} = useToast();
  const [newTier, setNewTier] = useState('');

  useEffect(() => {
    if (open) setNewTier(currentTier ?? '');
  }, [open, currentTier]);

  const changeTier = trpc.group.vbaMembers.productTierChange.useMutation({
    onSuccess: (result) => {
      toast({title: 'Coverage tier updated', description: `Now ${result.newTier}.`});
      onSuccess();
      onClose();
    },
    onError: (err) =>
      toast({title: 'Could not change tier', description: err.message, variant: 'destructive'}),
  });

  return (
    <MemberLifecycleDialogFrame
      open={open}
      onClose={onClose}
      title='Change coverage tier'
      description={
        <>
          Update the tier on {productName}. Tier is household composition, so changing it from the
          subscriber applies to the whole family. This writes to VBA and is logged.
        </>
      }
      cancelDisabled={changeTier.isPending}
      submitDisabled={!newTier || newTier === currentTier || changeTier.isPending}
      submitLabel={changeTier.isPending ? 'Saving…' : 'Change tier'}
      onSubmit={() => changeTier.mutate({groupId, id: memberId, newTier, productId})}
    >
      <div className='space-y-1.5'>
        <Label>Current tier</Label>
        <p className='text-sm text-brand-navy'>{currentTier?.trim() || '—'}</p>
      </div>

      <div className='space-y-1.5'>
        <Label>New tier</Label>
        <Select value={newTier} onValueChange={setNewTier}>
          <SelectTrigger>
            <SelectValue placeholder='Select a tier' />
          </SelectTrigger>
          <SelectContent>
            {TIER_OPTIONS.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </MemberLifecycleDialogFrame>
  );
}
